import React from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import {
  VpHookFormWrapper,
  VpAutoSave,
  VpMediaTitleInput,
  VpMediaDescriptionInput
} from '@vp-ui';
import { VpIcon } from '@vp-ui/components';
import VpMediaVersionList, { ORIGINAL_VERSION_ID } from '@vp-ui/components/VpMediaVersionList/VpMediaVersionList';
import config from '@src/config';
import { useUpdateMediaDetails, useMediaTableContext } from '@src/hooks';
import { extractThumbnailPath } from '@src/utils';
import { getMediaThumbnailEditingEnabled } from '@src/redux/modules/auth';
import { useVpSnackbar } from '@vp-ui/hooks/useVpSnackbar';
import {
  useCaptionPolling,
  useFetchCaptionText,
  useFetchMediaVersions
} from '@src/hooks/react-query/useCaptionsData';
import { useMediaBrandingBurns } from '@src/hooks/react-query/useBrandingData';

const VpMediaInfoPublish = ({ media, customizeThumbnailEnabled, history }) => {
  const { setUpdatingThumbnail, setSelectedSubmissionId } = useMediaTableContext();
  const { addSnackbarError } = useVpSnackbar();
  const { mutate: updateMediaData } = useUpdateMediaDetails();
  const mediaId = media?.mediaId;

  const { data: captionsResponse } = useCaptionPolling(mediaId);
  const captions = Array.isArray(captionsResponse) ? captionsResponse : [];
  const originalCaption = captions.find((c) => !c.isTranslation && c.status === 'completed');

  const { data: captionTextData } = useFetchCaptionText(originalCaption?.id);

  const { data: captionVersionsRaw } = useFetchMediaVersions(mediaId);
  const { data: brandingRaw } = useMediaBrandingBurns(mediaId);

  // The original is not a version in the backend — it is the media itself. It leads the list so
  // that publishing it stays possible, and visible, now that the buttons are per row.
  const versions = [
    {
      burnInId: ORIGINAL_VERSION_ID,
      status: 'completed',
      burnInUrl: media?.mediaUrl,
      label: 'Original'
    },
    ...(Array.isArray(brandingRaw) ? brandingRaw : []),
    ...(Array.isArray(captionVersionsRaw) ? captionVersionsRaw : [])
  ];
  const captionText = captionTextData?.text || (typeof captionTextData === 'string' ? captionTextData : '') || '';

  const firstName = media?.reviewerName?.split(' ')[0];
  const defaultTitle = media?.resolvedTitle || media?.mediaTitle || firstName || 'My Video';
  const defaultDescription = media?.resolvedDescription || media?.mediaDescription || captionText || 'Recorded with VideoPeel.';

  const defaultValues = {
    title: defaultTitle,
    description: defaultDescription,
    thumbnailUrl: media?.thumbnailUrl
  };

  const onSubmit = (values) => {
    const thumbnailToUse = values?.thumbnailUrl || media?.thumbnailUrl;
    const thumbnailUrl = extractThumbnailPath(thumbnailToUse);
    const data = {
      title: values?.title || '',
      description: values?.description || '',
      thumbnailUrl
    };
    updateMediaData({ data, mediaId: media?.mediaId });
  };

  const handleCustomizeThumbnail = () => {
    if (customizeThumbnailEnabled) {
      return setUpdatingThumbnail(media?.mediaId);
    }
    addSnackbarError({
      message:
        'Your current plan does not allow customizing video thumbnails. Please upgrade on the Billing page.',
      title: 'Error!',
    });
  };

  return (
    <VpHookFormWrapper key={captionText ? 'with-caption' : 'no-caption'} onSubmit={onSubmit}>
      <VpMediaTitleInput defaultValue={defaultTitle} />
      <VpMediaDescriptionInput defaultValue={defaultDescription} />
      <Stack direction="row" alignItems="center" gap={1}>
        <Button
          variant="outlined"
          color="neutral"
          onClick={handleCustomizeThumbnail}
          sx={{ gap: '10px' }}
        >
          <VpIcon icon="Image" sx={{ width: '16px' }} /> Customize Thumbnail
        </Button>
        {/* TODO: replace with publish-specific help center URL when available */}
        <Tooltip title="Learn more about publishing">
          <IconButton
            component="a"
            href={config.helpCenterLink}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <VpIcon icon="HelpCircle" size="18px" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Versions ──────────────────────────────────────────────────────────
          Publishing used to be two buttons that silently acted on the original. Every version
          is a row now, so which one goes out is a choice the user makes rather than a default
          they cannot see.

          Deliberately no `onSuccess` navigation. The single button this replaced closed the
          dialog and jumped to the library, which made sense when there was exactly one thing
          to publish. With a list, publishing the original and then the captioned cut is the
          normal case, and being thrown out of the dialog after the first one makes it two
          trips. The snackbar names the library it went to; "Manage Libraries" below is the way
          there. */}
      <Stack gap={1} sx={{ mt: 1 }}>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          Versions
        </Typography>
        <VpMediaVersionList
          versions={versions}
          mediaId={media?.mediaId}
          actions={['library', 'plugin']}
        />
        <Stack direction="row" gap={1} alignItems="center">
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setSelectedSubmissionId(null);
              history.push('/libraries');
            }}
          >
            Manage Libraries
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setSelectedSubmissionId(null);
              history.push('/plugins');
            }}
          >
            Manage Plugins
          </Button>
        </Stack>
      </Stack>

      <VpAutoSave onSubmit={onSubmit} defaultValues={defaultValues} />
    </VpHookFormWrapper>
  );
};

VpMediaInfoPublish.propTypes = {
  media: PropTypes.shape({
    mediaTitle: PropTypes.string,
    mediaDescription: PropTypes.string,
    resolvedTitle: PropTypes.string,
    resolvedDescription: PropTypes.string,
    thumbnailUrl: PropTypes.string,
    mediaId: PropTypes.string,
    mediaUrl: PropTypes.string,
    reviewerName: PropTypes.string
  }),
  customizeThumbnailEnabled: PropTypes.bool,
  history: PropTypes.object
};

const mapStateToProps = (state) => ({
  customizeThumbnailEnabled: getMediaThumbnailEditingEnabled(state)
});

const mapDispatchToProps = {};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(VpMediaInfoPublish));



// WEBPACK FOOTER //
// ./src/vp-ui/dialogs/VpMediaSubmissionDetailsDialog/VpMediaInfoPublish.jsx